import React, { useEffect, useState } from 'react';
import { errorToast, successToast } from '../../utils/helper';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Pagination from '../../component/common/Pagination/Pagination';
import { Api } from '../../api';
import { PER_PAGE } from '../../utils/constants';

const ViewEnrolledUsersModal = ({ isOpen, onClose, company, onToggleUser }) => {
  const [loader, setLoader] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage] = useState(10);

  useEffect(() => {
    if (isOpen && company) {
      fetchEnrollments(1, perPage, '');
    }
  }, [isOpen, company]);

  useEffect(() => {
    if (searchTerm !== undefined && isOpen) {
      const delayDebounceFn = setTimeout(() => {
        fetchEnrollments(1, perPage, searchTerm);
      }, 800);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchTerm]);

  const fetchEnrollments = async (page, limit, search) => {
    if (!company?.companyId) return;

    setLoader(true);
    try {
      const response = await Api.getSalarySacrificeEnrolledUsers(
        company.companyId,
        page,
        limit,
        search
      );

      if (response?.data?.meta?.code === 1) {
        setEnrollments(response?.data?.data || []);
        setTotalCount(response?.data?.totalRecords || 0);
        setCurrentPage(page);
      } else {
        errorToast(response?.data?.message || 'Failed to fetch enrolled users');
        setEnrollments([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      errorToast('Failed to fetch enrolled users');
      setEnrollments([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  const handleToggleStatus = async (enrollmentId, currentStatus) => {
    setLoader(true);
    try {
      const reqBody = {
        enrollmentId,
        isActive: !currentStatus
      };

      const response = await Api.toggleSalarySacrificeEnrollment(reqBody);

      if (response?.data?.meta?.code === 1) {
        successToast(
          `User ${!currentStatus ? 'activated' : 'deactivated'} successfully`
        );
        fetchEnrollments(currentPage, perPage, searchTerm);
        onToggleUser();
      } else {
        errorToast(response?.data?.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      errorToast('Failed to update user status');
    } finally {
      setLoader(false);
    }
  };

  const handlePagination = (page) => {
    fetchEnrollments(page, perPage, searchTerm);
  };

  const handleClose = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setEnrollments([]);
    setTotalCount(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {loader && <Loader />}
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-xl w-full max-w-4xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-shoorah-primary dark:text-white">
              Enrolled Users - {company?.companyName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {company?.providerName || 'Shoorah'} Scheme
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Enrolled</p>
            <p className="text-xl font-bold text-shoorah-primary dark:text-white">
              {totalCount}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">
              {enrollments.filter((e) => e.isActive).length}
            </p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">Inactive</p>
            <p className="text-xl font-bold text-red-600 dark:text-red-400">
              {enrollments.filter((e) => !e.isActive).length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <SearchInput
            id="searchEnrollments"
            name="searchEnrollments"
            type="text"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by user name or email..."
          />
        </div>

        {/* Users Table */}
        <div className="border border-gray-200 dark:border-shoorah-darkBorder rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Enrolled Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-shoorah-darkBgColor divide-y divide-gray-200 dark:divide-shoorah-darkBorder">
                {enrollments.length > 0 ? (
                  enrollments.map((enrollment) => (
                    <tr key={enrollment._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {enrollment.userId?.name || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {enrollment.userId?.email || 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {enrollment.enrollmentDate
                          ? new Date(enrollment.enrollmentDate).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            enrollment.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {enrollment.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={enrollment.isActive}
                            onChange={() =>
                              handleToggleStatus(enrollment._id, enrollment.isActive)
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-shoorah-primary/20 dark:peer-focus:ring-shoorah-primary/30 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-shoorah-primary"></div>
                        </label>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {searchTerm
                        ? 'No users found matching your search.'
                        : 'No enrolled users found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {enrollments.length > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={perPage}
              onPageChange={handlePagination}
            />
          </div>
        )}

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewEnrolledUsersModal;
